import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import GlobalStyles from '@mui/material/GlobalStyles';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';
import { AppBar, Toolbar } from '../components/bars';
import { Button } from '../components/input';

// Section anchors on the landing page. Add Approach (#approach) and
// About (#about) here once those sections exist.
const navLinks = [
    { label: 'Services', href: '#services' },
];

const contactHref = '#contact';

const navLink = {
    fontSize: 16,
    color: 'text.primary',
    ml: 4,
    '&:hover': {
        color: 'secondary.light',
    },
    '&:focus-visible': {
        outline: '2px solid',
        outlineColor: 'secondary.main',
        outlineOffset: 4,
    },
};

function MenuIcon(props: SvgIconProps) {
    return (
        <SvgIcon {...props}>
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
        </SvgIcon>
    );
}

function AppAppBar() {
    const [menuOpen, setMenuOpen] = React.useState(false);
    const closeMenu = () => setMenuOpen(false);

    return (
        <div>
            {/* Keep anchored sections from landing under the fixed bar. */}
            <GlobalStyles
                styles={{
                    html: { scrollBehavior: 'smooth', scrollPaddingTop: 70 },
                    '@media (prefers-reduced-motion: reduce)': {
                        html: { scrollBehavior: 'auto' },
                    },
                }}
            />
            <AppBar position="fixed">
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Link
                        variant="h6"
                        underline="none"
                        color="inherit"
                        href="/"
                        sx={{ fontSize: 24, display: 'flex', alignItems: 'center' }}
                    >
                        <Box
                            component="img"
                            src="/logo.svg"
                            alt=""
                            sx={{ width: 32, height: 32, mr: 1.5 }}
                        />
                        {'Mnemetic'}
                    </Link>
                    <Box
                        component="nav"
                        aria-label="Main"
                        sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}
                    >
                        {navLinks.map((link) => (
                            <Link key={link.href} underline="none" href={link.href} sx={navLink}>
                                {link.label}
                            </Link>
                        ))}
                        <Button
                            color="secondary"
                            variant="contained"
                            size="small"
                            href={contactHref}
                            sx={{ ml: 4 }}
                        >
                            Contact
                        </Button>
                    </Box>
                    <IconButton
                        color="inherit"
                        aria-label="Open menu"
                        aria-controls="mobile-nav"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen(true)}
                        sx={{ display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>
            <Drawer
                id="mobile-nav"
                anchor="right"
                open={menuOpen}
                onClose={closeMenu}
                PaperProps={{ sx: { width: 240, backgroundColor: 'primary.main' } }}
                sx={{ display: { md: 'none' } }}
            >
                <Box component="nav" aria-label="Main" sx={{ pt: 2 }}>
                    <List>
                        {navLinks.map((link) => (
                            <ListItemButton
                                key={link.href}
                                component="a"
                                href={link.href}
                                onClick={closeMenu}
                            >
                                <ListItemText primary={link.label} />
                            </ListItemButton>
                        ))}
                    </List>
                    <Box sx={{ px: 2, pt: 1 }}>
                        <Button
                            color="secondary"
                            variant="contained"
                            size="small"
                            fullWidth
                            href={contactHref}
                            onClick={closeMenu}
                        >
                            Contact
                        </Button>
                    </Box>
                </Box>
            </Drawer>
            <Toolbar />
        </div>
    );
}

export default AppAppBar;
